// ============================================================================
// PROCESS INVESTIGATIONS — guided Q&A over the full process data payload.
// Grounded in evidence collected by Process Advisor (metrics, variants, 
// transitions, SLA, field quality, samples).
// ============================================================================

import React, { useMemo, useRef, useState } from 'react';
import { getAIModelConfig, getInvokeAIProvider } from '@advisor-core/lib/aiModelConfig';
import { invokeWebAI } from '@advisor-core/lib/webAiClient';
import {
  Search,
  Sparkles,
  BookOpen,
  Send,
  Copy,
  Database,
  MessageSquare,
  CheckCircle2,
  AlertTriangle,
  ChevronRight,
} from 'lucide-react';

import { apiProvider } from '@process-advisor/process-miner/api/apiProvider';
import {
  getModelMetrics,
  getModelMetricsTotal,
  getAppVersion,
  getIssueTypes,
  getProcessModelTitle,
  taskJSON,
  getModelID,
  getFieldUsage,
  getStatusTypeSummary,
  getStatusTypeMappings,
  getAttributePrivacyRules,
  getActivityType,
  getActivityContext,
} from '@process-advisor/process-miner/processMiner/processMiner';
import { buildConsultantRuntimeArgs } from '@process-advisor/process-miner/consultant/consultantSharedRuntimeArgs';
import { buildSharedConsultantPayload } from '@process-advisor/process-miner/consultant/consultantSharedAiPayload';
import { injectOrganizationContext } from '@process-advisor/process-miner/consultant/organizationProfile';
import { invokeProcessOpenAIWithBilling } from '@process-advisor/process-miner/consultant/aiBillingAccess';
import ExportModal from '@process-advisor/process-miner/consultant/ExportModal';

let forgeModule = null;
const loadForgeModule = async () => {
  if (!forgeModule) forgeModule = await import('@forge/bridge');
  return forgeModule;
};

const POLL_MS = 1200;
const MAX_POLL = 120;

const toInvokeProvider = (p) =>
  p === 'gemini' ? 'Gemini' : p === 'claude' ? 'Claude' : 'ChatGPT';

const cleanAndParseJSON = (raw) => {
  let s = String(raw || '{}')
    .replace(/^```json\s*/i, '')
    .replace(/^```\s*/i, '')
    .replace(/```\s*$/i, '')
    .trim();
  const lb = s.lastIndexOf('}');
  if (lb !== -1) s = s.substring(0, lb + 1);
  try { return JSON.parse(s); } catch (_) {}
  return {
    answer_summary: s || 'The AI response could not be parsed as structured JSON.',
    key_points: [],
    evidence_used: [],
    recommended_actions: [],
    limits: ['The response was returned in an unexpected format.'],
    follow_up_questions: [],
  };
};

const getDirectGeminiKey = () => {
  try {
    if (typeof API_KEY !== 'undefined' && API_KEY) return API_KEY;
  } catch (_) {}
  return undefined;
};

const callDirectGemini = async ({ prompt, model }) => {
  if (getAppVersion() === 'WEB') {
    const result = await invokeWebAI({ prompt, reportLabel: 'Investigation Question', responseFormat: 'json' });
    return cleanAndParseJSON(result?.text || '{}');
  }
  const configuredModel = getAIModelConfig().model;
  const geminiModel = model && String(model).startsWith('gemini') ? model : configuredModel;
  const key = getDirectGeminiKey();
  const res = await fetch(
    `https://generativelanguage.googleapis.com/v1beta/models/${geminiModel}:generateContent?key=${key}`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        contents: [{ parts: [{ text: `${prompt}\n\nReturn valid JSON only. Do not wrap the response in markdown fences.` }] }],
        generationConfig: { responseMimeType: 'application/json', maxOutputTokens: 32768 },
      }),
    }
  );

  if (!res.ok) {
    const body = await res.text().catch(() => '');
    throw new Error(`Gemini request failed: ${res.status} ${res.statusText} ${body}`);
  }

  const data = await res.json();
  return cleanAndParseJSON(data?.candidates?.[0]?.content?.parts?.[0]?.text || '{}');
};

const safeNumber = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const n = Number(String(value).replace(/,/g, '').replace(/%/g, '').trim());
  return Number.isFinite(n) ? n : null;
};

const compactJson = (value, max = 120000) => {
  const json = JSON.stringify(value || {}, null, 2);
  // The Investigation prompt now uses a compacted evidence payload, so truncation
  // should be rare. Keep this as a defensive guard rather than normal behaviour.
  if (json.length <= max) return json;
  console.warn('[Process Advisor][Investigations] Prompt payload exceeded size guard and was truncated', {
    length: json.length,
    max,
  });
  return `${json.slice(0, max)}\n... [payload truncated for prompt size]`;
};

const normaliseQuestion = (q) => String(q || '').replace(/\s+/g, ' ').trim();

const takeArray = (value, limit = 10) => Array.isArray(value) ? value.slice(0, limit) : [];

const compactFieldSignal = (field = {}) => ({
  field: field.field || field.field_name || field.attribute || field.label || null,
  field_name: field.field_name || field.field || field.attribute || field.label || null,
  populated_items: field.populated_items ?? null,
  population_rate: field.population_rate ?? field.populated_pct ?? null,
  distinct_values: field.distinct_values ?? null,
  dominance_pct: field.dominance_pct ?? null,
  classification_strength: field.classification_strength || null,
  top_values: takeArray(field.top_values || field.values, 8).map((v) => ({
    label: v.label ?? v.value ?? null,
    count: v.count ?? v.tasks ?? null,
    pct: v.perc_total_tasks ?? v.pct ?? v.percent ?? null,
  })),
});

const compactCategoryBreakdowns = (categoryBreakdowns) => {
  if (!categoryBreakdowns) return null;
  if (Array.isArray(categoryBreakdowns)) return categoryBreakdowns.slice(0, 8).map(compactFieldSignal);
  if (typeof categoryBreakdowns === 'object') {
    return Object.entries(categoryBreakdowns).slice(0, 8).map(([field, data]) => ({
      field,
      ...compactFieldSignal({ ...(data || {}), field }),
    }));
  }
  return null;
};

const compactSlaSignals = (sla = {}) => {
  if (!sla || typeof sla !== 'object') return sla || null;
  return {
    available: sla.available,
    total_sla_records: sla.total_sla_records,
    tasks_with_sla: sla.tasks_with_sla,
    task_sla_coverage_percent: sla.task_sla_coverage_percent,
    breached_items: sla.breached_items,
    breached_items_percent: sla.breached_items_percent,
    items_without_sla_percent: sla.items_without_sla_percent,
    avg_business_elapsed_hours: sla.avg_business_elapsed_hours,
    completed_sla_records: sla.completed_sla_records,
    active_sla_records: sla.active_sla_records,
    paused_sla_records: sla.paused_sla_records,
    cancelled_sla_records: sla.cancelled_sla_records,
    top_sla_names: takeArray(sla.top_sla_names, 10),
    top_sla_stages: takeArray(sla.top_sla_stages, 10),
  };
};

const compactInvestigationPayload = (inputPayload = {}) => {
  const activityModel = inputPayload.activity_model || null;
  const workload = inputPayload.workload_signals || {};
  const variantSignals = inputPayload.variant_signals || {};
  const transitionSignals = inputPayload.transition_signals || {};

  return {
    context: inputPayload.context || null,
    process_summary: inputPayload.process_summary || null,
    canonical_health: inputPayload.canonical_health || null,
    data_coverage: inputPayload.data_coverage || null,
    time_intelligence: inputPayload.time_intelligence || null,
    activity_context: inputPayload.activity_context || null,
    activity_model: activityModel ? {
      summary: activityModel.summary || null,
      activities: takeArray(activityModel.activities, 14).map((a) => ({
        activity: a.activity || a.status || null,
        operational_type: a.operational_type || null,
        time_bucket: a.time_bucket || null,
        confidence: a.confidence ?? null,
      })),
    } : null,
    transition_signals: {
      top_transitions: takeArray(transitionSignals.top_transitions, 12),
      top_delay_transitions: takeArray(transitionSignals.top_delay_transitions, 12),
      top_rework_transitions: takeArray(transitionSignals.top_rework_transitions, 12),
    },
    variant_signals: {
      top_variants: takeArray(variantSignals.top_variants, 15),
      rework_variants: takeArray(variantSignals.rework_variants, 8),
      long_tail_summary: variantSignals.long_tail_summary || null,
    },
    sla_signals: compactSlaSignals(inputPayload.sla_signals || null),
    workload_signals: {
      attribute_usage: takeArray(
        workload.attribute_usage || workload.field_usage_highlights,
        8
      ).map(compactFieldSignal),
    },
    roi_estimate: inputPayload.roi_estimate || null,
  };
};

const QUESTION_LIBRARY = [
  {
    group: 'Leadership',
    questions: [
      'What are the three most important findings leadership should act on?',
      'What is the simplest executive summary of this process assessment?',
      'What should be fixed first and why?',
      'What risks are most likely to affect service performance or customer experience?',
      'Which findings have enough evidence to take to an executive steering group?',
      'What is the strongest evidence that this process needs management attention?',
    ],
  },
  {
    group: 'SLA & Service Performance',
    questions: [
      'What appears to be driving SLA breaches or SLA risk?',
      'Which activities, resolver groups, paths, or attributes are most associated with poor SLA outcomes?',
      'Where should SLA governance be tightened first?',
      'What would improve predictability of resolution time?',
      'Which SLA names or stages should be reviewed first?',
      'Where does elapsed business time look least controlled?',
    ],
  },
  {
    group: 'Process Optimisation',
    questions: [
      'Which routing paths or process variants should be simplified first?',
      'Where are the biggest bottlenecks, waits, or handoff issues?',
      'Which activities look like automation or standardisation candidates?',
      'What process redesign would have the highest practical impact?',
      'Which high-volume path should be treated as the target standard flow?',
      'Where should ownership, assignment, or routing rules be simplified?',
    ],
  },
  {
    group: 'Data Quality & Governance',
    questions: [
      'Which fields or attributes are weakening analysis confidence?',
      'What data quality issues should be corrected before automation?',
      'Which fields should be standardised to improve reporting and governance?',
      'What evidence suggests the process is fragmented or poorly controlled?',
      'Which fields are strong enough to use for segmentation and improvement planning?',
      'What data should be cleaned before building automation or AI recommendations?',
    ],
  },
  {
    group: 'Consultant Workshop',
    questions: [
      'What questions should we ask process owners in a workshop?',
      'What evidence should be shown to validate these findings with stakeholders?',
      'What are the likely objections and how should we respond?',
      'What should go into a 30-day improvement plan?',
      'Which findings need stakeholder validation before they are presented as conclusions?',
      'What workshop agenda would best test the evidence in this model?',
    ],
  },
];

const buildInvestigationPayload = () => {
  const { runtimeArgs, debug } = buildConsultantRuntimeArgs({
    issueTypes: getIssueTypes ? getIssueTypes() : [],
    metrics: getModelMetrics ? getModelMetrics() : [],
    metricsTotal: typeof getModelMetricsTotal === 'function' ? getModelMetricsTotal() : null,
    processSteps: apiProvider.processStepsTable ? (apiProvider.processStepsTable() || []) : [],
    variants: apiProvider.variantsTable ? (apiProvider.variantsTable() || []) : [],
    rawSampleItems: taskJSON ? taskJSON(1000) : [],
    privacyRules: getAttributePrivacyRules ? getAttributePrivacyRules() : {},
    fieldUsage: getFieldUsage ? getFieldUsage() : {},
    statusTypeSummary: getStatusTypeSummary ? getStatusTypeSummary() : null,
    statusTypeMappings: getStatusTypeMappings ? getStatusTypeMappings() : [],
    activityType: typeof getActivityType === 'function' ? getActivityType() : null,
    activityContext: typeof getActivityContext === 'function' ? getActivityContext() : null,
    extra: {
      investigation_mode: true,
      model_id: getModelID ? getModelID() : null,
      model_title: getProcessModelTitle ? getProcessModelTitle() : '',
    },
  });
  const inputPayload = buildSharedConsultantPayload(runtimeArgs);
  return { runtimeArgs, inputPayload, debug };
};

const buildDataDrivenQuestions = (payload = {}) => {
  const questions = [];
  const summary = payload.process_summary || {};
  const health = payload.canonical_health || {};
  const sla = payload.sla_signals || {};
  const dataCoverage = payload.data_coverage || {};
  const workload = payload.workload_signals || {};

  const variants = safeNumber(summary.variants_total);
  const totalItems = safeNumber(summary.total_items);
  const rework = safeNumber(summary.rework_rate_percent);
  const breachPct = safeNumber(sla.breached_items_percent);
  const noSlaPct = safeNumber(sla.items_without_sla_percent || sla.no_sla_items_percent);
  const healthScore = safeNumber(health.score_display || health.score);

  const pushQuestion = (text, trigger) => {
    questions.push({
      text: String(text).replace(/\s+/g, ' ').trim(),
      trigger
    });
  };

  // 1. COMPOUND INSIGHT: Fragmentation Intersects with Rework Loops
  if (variants && variants >= 30 && rework && rework >= 12) {
    pushQuestion(
      `Does the operational complexity of ${variants} distinct process variants directly fuel our ${Math.round(rework)}% rework loop, and where can we standardize?`,
      'Complexity Loop'
    );
  } 
  // 2. TIERED INSIGHT: Process Fragmentation & Standard Flow Enforcements
  else if (variants && totalItems && variants > Math.max(25, totalItems * 0.15)) {
    pushQuestion(
      `Critical process fragmentation detected: Why are there ${variants} paths handling just ${totalItems} cases, and which standard flow should we enforce?`,
      'Critical Fragmentation'
    );
  } else if (variants && variants >= 30) {
    pushQuestion(
      `What is driving the ${variants} process variants, and is the fragmentation material?`,
      'Process Variance'
    );
  }

  // 3. TIERED INSIGHT: Incident Resolution SLA Breaches
  if (breachPct) {
    if (breachPct >= 25) {
      pushQuestion(
        `Critical SLA exposure: What is driving the severe ${Math.round(breachPct)}% breach rate, and which operational choke points require immediate intervention?`,
        'Severe SLA Risk'
      );
    } else if (breachPct >= 10) {
      pushQuestion(
        `What is driving the ${Math.round(breachPct)}% SLA breach rate and where should we intervene first?`,
        'SLA Breach Rate'
      );
    }
  } else if (sla.available || dataCoverage.sla_metrics) {
    pushQuestion(
      'What does the SLA data tell us about service risk and delivery predictability?',
      'SLA Metrics Assessment'
    );
  }

  // 4. SIGNAL: Governance Tracking Blind Spots
  if (noSlaPct && noSlaPct >= 20) {
    pushQuestion(
      `Why do ${Math.round(noSlaPct)}% of items bypass standard SLA logic, and what operational blind spots does this lack of tracking create?`,
      'Governance Gap'
    );
  }

  // 5. SIGNAL: Standalone Rework/Waste Framework
  if (rework && rework >= 10 && (!variants || variants < 30 || rework < 12)) {
    pushQuestion(
      `What is causing the ${Math.round(rework)}% rework rate and which process loops matter most?`,
      'Rework & Waste'
    );
  }

  // 6. SIGNAL: Operational Group Transitions and Routing Handoffs
  const attributeUsage = Array.isArray(workload.attribute_usage)
    ? workload.attribute_usage
    : (Array.isArray(workload.field_usage_highlights) ? workload.field_usage_highlights : []);
  const resolverLike = attributeUsage.find((f) => /assignment|resolver|group|team|owner/i.test(String(f?.field || f?.field_name || f?.label || '')));
  if (resolverLike) {
    const fieldName = resolverLike.field || resolverLike.field_name || resolverLike.label || 'resolver group';
    pushQuestion(
      `Which specific ${fieldName} handoffs contribute most heavily to routing friction or process delay?`,
      'Routing Handoffs'
    );
  }

  // 7. SIGNAL: Data Quality and Structural Ingest Hygiene
  const sparseFields = attributeUsage
    .filter((f) => {
      const n = safeNumber(f?.population_rate ?? f?.populated_pct ?? f?.populated_percent);
      return n !== null && n > 0 && n < 70;
    })
    .map((f) => f.field || f.field_name || f.label)
    .filter(Boolean);
  if (sparseFields.length > 0) {
    pushQuestion(
      `Which missing or sparse attributes (e.g., ${sparseFields.slice(0, 2).join(', ')}) are weakening our analysis confidence before automation?`,
      'Data Quality'
    );
  }

  // 8. SIGNAL: Baseline Health Architecture Metrics
  if (healthScore && healthScore < 5) {
    pushQuestion(
      `With a process health score of ${healthScore}/10, what structural changes are mandatory to move this process to an acceptable baseline?`,
      'Process Health'
    );
  }

  // 9. SIGNAL: High-Delay Step Transitions
  const topDelayTransitions = Array.isArray(payload.transition_signals?.top_delay_transitions) ? payload.transition_signals.top_delay_transitions : [];
  if (topDelayTransitions.length > 0) {
    const transition = topDelayTransitions[0]?.transition || [topDelayTransitions[0]?.from, topDelayTransitions[0]?.to].filter(Boolean).join(' → ');
    if (transition) {
      pushQuestion(
        `Why is the transition "${transition}" exhibiting extreme delays, and what specific action will clear this bottleneck?`,
        'Process Bottleneck'
      );
    }
  }

  // 10. STANDARD BASELINES
  const topVariants = Array.isArray(payload.variant_signals?.top_variants) ? payload.variant_signals.top_variants : [];
  if (topVariants.length > 0) {
    pushQuestion(
      'Which of the top variants should become the standard path, and which variants represent exception handling?',
      'Standardization Baseline'
    );
  }

  const slaNames = Array.isArray(sla.top_sla_names) ? sla.top_sla_names : [];
  if (slaNames.length > 0) {
    pushQuestion(
      'Which SLA names are creating the most service exposure and why?',
      'SLA Governance'
    );
  }

  pushQuestion('What are the highest-value improvement actions supported by the data?', 'Opportunity Strategy');
  pushQuestion('What should we validate with process owners before making changes?', 'Stakeholder Validation');

  const seen = new Set();
  return questions.filter(q => {
    if (!q.text || seen.has(q.text)) return false;
    seen.add(q.text);
    return true;
  });
};

const buildInvestigationPrompt = ({ question, payloadBundle, priorTurns = [] }) => {
  const { inputPayload, runtimeArgs, debug } = payloadBundle;
  const advisorEvidencePayload = inputPayload || {};
  const promptPayload = {
    model: {
      title: getProcessModelTitle ? getProcessModelTitle() : '',
      id: getModelID ? getModelID() : '',
      app_version: getAppVersion ? getAppVersion() : '',
      source_system: advisorEvidencePayload?.activity_context?.platform || runtimeArgs?.activityContext?.platform || '',
    },
    advisor_evidence_payload: advisorEvidencePayload,
    data_quality_and_scope: {
      coverage: advisorEvidencePayload?.data_coverage || null,
      privacy: debug?.privacy?.summary || null,
      build_summary: debug?.summary || null,
      prompt_payload_note: 'This prompt uses the same canonical Advisor evidence payload as the other AI content. sample_evidence.rows are random/scoped sample items and should only be used when item-level examples or validation are necessary.',
    },
    previous_questions: priorTurns.slice(-3),
  };

  return `You are Process Advisor for ServiceNow. You answer business questions by transforming collected ServiceNow process data into actionable insights.

CORE BEHAVIOUR
- Use only the supplied process data, process mining metrics, SLA signals, field usage, transitions, variants, and sample items.
- Do not invent data, benchmarks, ticket details, root causes, or calculations not supported by the payload.
- Be direct: answer the user's question, explain what evidence supports it, and recommend practical next actions.
- Separate observed facts from interpretation.
- Where the evidence is incomplete, state the limitation and what data or workshop question would validate it.
- This is not a generic chatbot. Stay grounded in this specific ServiceNow process model.
- The sample_evidence.rows section contains random/scoped sample work items. Use it only when item-level examples, validation, or data-quality checks are needed. Do not let sample rows override aggregate metrics, variants, transitions, SLA signals, or attribute usage.

RESPONSE FORMAT
Return exactly one JSON object with this structure:
{
  "answer_summary": "short direct answer",
  "key_points": [
    { "label": "", "detail": "", "evidence": "specific metric/data point used" }
  ],
  "recommended_actions": [
    { "action": "", "why": "", "priority": "Now|Next|Later" }
  ],
  "evidence_used": ["specific metrics, fields, variants, transitions, SLA signals, or sample evidence used"],
  "limits": ["anything the data cannot prove"],
  "follow_up_questions": ["", ""]
}

USER QUESTION
${question}

PROCESS ADVISOR CANONICAL EVIDENCE PAYLOAD
${compactJson(promptPayload)}`;
};

const PriorityBadge = ({ priority }) => {
  const cfg = {
    Now: { bg: '#FFEBE6', color: '#DE350B' },
    Next: { bg: '#FFF0B3', color: '#974F0C' },
    Later: { bg: '#E9F2FF', color: '#0C66E4' },
  }[priority] || { bg: '#F4F5F7', color: '#42526E' };
  return <span className="pa-inv-priority" style={{ background: cfg.bg, color: cfg.color }}>{priority || 'Next'}</span>;
};

const AnswerView = ({ answer, onAsk }) => {
  if (!answer) return null;
  const keyPoints = Array.isArray(answer.key_points) ? answer.key_points : [];
  const actions = Array.isArray(answer.recommended_actions) ? answer.recommended_actions : [];
  const evidence = Array.isArray(answer.evidence_used) ? answer.evidence_used : [];
  const limits = Array.isArray(answer.limits) ? answer.limits : [];
  const followUps = Array.isArray(answer.follow_up_questions) ? answer.follow_up_questions.filter(Boolean) : [];

  return (
    <div className="pa-inv-answer">
      {answer.answer_summary && (
        <div className="pa-inv-summary-card">
          <div className="pa-inv-summary-label">
            <Sparkles size={14} />
            <span>AI answer</span>
          </div>
          <p className="pa-inv-summary">{answer.answer_summary}</p>
        </div>
      )}

      {keyPoints.length > 0 && (
        <section className="pa-inv-answer-section">
          <div className="pa-inv-section-label">
            <span>Supporting evidence</span>
            <em>{keyPoints.length} point{keyPoints.length === 1 ? '' : 's'}</em>
          </div>
          <div className="pa-inv-list">
            {keyPoints.map((item, idx) => (
              <div className="pa-inv-keypoint" key={idx}>
                <strong>{item.label || `Point ${idx + 1}`}</strong>
                <p>{item.detail || item.evidence || ''}</p>
                {item.evidence && <small>{item.evidence}</small>}
              </div>
            ))}
          </div>
        </section>
      )}

      {actions.length > 0 && (
        <section className="pa-inv-answer-section">
          <div className="pa-inv-section-label">
            <span>Recommended actions</span>
            <em>{actions.length} action{actions.length === 1 ? '' : 's'}</em>
          </div>
          <div className="pa-inv-list">
            {actions.map((item, idx) => (
              <div className="pa-inv-action" key={idx}>
                <PriorityBadge priority={item.priority} />
                <div>
                  <strong>{item.action || `Action ${idx + 1}`}</strong>
                  {item.why && <p>{item.why}</p>}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {evidence.length > 0 && (
        <section className="pa-inv-answer-section pa-inv-evidence-section">
          <div className="pa-inv-section-label">
            <span>Evidence used</span>
          </div>
          <ul className="pa-inv-bullets">{evidence.map((item, idx) => <li key={idx}>{item}</li>)}</ul>
        </section>
      )}

      {limits.length > 0 && (
        <section className="pa-inv-limits">
          <div className="pa-inv-section-label warning">
            <span>Limits / validation needed</span>
          </div>
          <ul>{limits.map((item, idx) => <li key={idx}>{item}</li>)}</ul>
        </section>
      )}

      {followUps.length > 0 && (
        <section className="pa-inv-answer-section">
          <div className="pa-inv-section-label">
            <span>Follow-up questions</span>
          </div>
          <div className="pa-inv-chip-row">
            {followUps.map((q, idx) => <button key={idx} type="button" onClick={() => onAsk(q)}>{q}</button>)}
          </div>
        </section>
      )}
    </div>
  );
};

export default function ProcessInvestigations({ processLabel = 'Process', entityLabel = 'Work Item', aiModelConfig }) {
  const [customQuestion, setCustomQuestion] = useState('');
  const [selectedQuestion, setSelectedQuestion] = useState('');
  const [answer, setAnswer] = useState(null);
  const [history, setHistory] = useState([]);
  const [expandedLibraryGroups, setExpandedLibraryGroups] = useState(() => ({}));
  const [isAsking, setIsAsking] = useState(false);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const pollRef = useRef(null);

  const payloadBundle = useMemo(() => {
    try { return buildInvestigationPayload(); }
    catch (err) {
      console.error('[ProcessInvestigations] build payload failed:', err);
      return { runtimeArgs: {}, inputPayload: {}, debug: {} };
    }
  }, []);

  const dataDrivenQuestions = useMemo(() => buildDataDrivenQuestions(payloadBundle.inputPayload || {}), [payloadBundle]);

  const toggleLibraryGroup = (group) => {
    setExpandedLibraryGroups((prev) => ({ ...prev, [group]: !prev[group] }));
  };

  const ask = async (rawQuestion) => {
    const questionString = typeof rawQuestion === 'object' && rawQuestion !== null 
      ? rawQuestion.text 
      : rawQuestion;

    const q = normaliseQuestion(questionString || customQuestion || selectedQuestion);
    if (!q || isAsking) return;
    setSelectedQuestion(q);
    setCustomQuestion('');
    setAnswer(null);
    setError('');
    setIsAsking(true);

    const priorTurns = history.map((turn) => ({ question: turn.question, answer_summary: turn.answer?.answer_summary || '' }));
    const advisorEvidencePayload = payloadBundle?.inputPayload || {};
    const prompt = await injectOrganizationContext(buildInvestigationPrompt({ question: q, payloadBundle, priorTurns }));

    // Lightweight diagnostic only. Do not log or expose full prompts/payloads in production.
    try {
      window.__PROCESS_ADVISOR_LAST_INVESTIGATION_DEBUG__ = {
        question: q,
        generated_at: new Date().toISOString(),
        prompt_length: prompt.length,
        app_version: typeof getAppVersion === 'function' ? getAppVersion() : '',
        model_title: typeof getProcessModelTitle === 'function' ? getProcessModelTitle() : '',
        truncated_by_prompt_builder: prompt.includes('[payload truncated for prompt size]'),
      };
    } catch (_) {}

    const cfg = getAIModelConfig(aiModelConfig);
    const provider = cfg.provider || 'gemini';
    const model = cfg.model;
    const temperature = cfg.temperature ?? 0.25;

    try {
      const appVersion = typeof getAppVersion === 'function' ? getAppVersion() : 'FORGE';
      let parsed = null;

      if (appVersion === 'SNOW' || appVersion === 'WEB') {
        parsed = await callDirectGemini({ prompt, model });
      } else {
        const { invoke } = await loadForgeModule();
        const response = await invokeProcessOpenAIWithBilling({
          invoke,
          reportLabel: 'Investigation Question',
          payload: { prompt, aiProvider: toInvokeProvider(provider), model, temperature },
        });
        if (!response?.requestId) throw new Error('No requestId returned from AI request');
        parsed = await new Promise((resolve, reject) => {
          let pollCount = 0;
          pollRef.current = setInterval(async () => {
            pollCount += 1;
            if (pollCount > MAX_POLL) {
              clearInterval(pollRef.current);
              reject(new Error('Timed out waiting for AI response'));
              return;
            }
            try {
              const statusData = await invoke('checkStatus', { requestId: response.requestId });
              if (statusData?.status === 'completed') {
                clearInterval(pollRef.current);
                resolve(cleanAndParseJSON(statusData.result || '{}'));
              } else if (statusData?.status === 'failed') {
                clearInterval(pollRef.current);
                reject(new Error(statusData?.error || 'AI request failed'));
              }
            } catch (err) {
              clearInterval(pollRef.current);
              reject(err);
            }
          }, POLL_MS);
        });
      }

      setAnswer(parsed);
      setHistory((prev) => [{ question: q, answer: parsed, at: new Date().toISOString() }, ...prev].slice(0, 10));
    } catch (err) {
      console.error('[ProcessInvestigations] ask failed:', err);
      setError(err?.message || 'The investigation answer could not be generated.');
    } finally {
      setIsAsking(false);
    }
  };

  const copyAnswer = async () => {
    if (!answer) return;
    const text = [
      `Question: ${selectedQuestion}`,
      '',
      answer.answer_summary || '',
      '',
      ...(Array.isArray(answer.key_points) ? answer.key_points.map((p) => `- ${p.label || 'Point'}: ${p.detail || p.evidence || ''}`) : []),
      '',
      ...(Array.isArray(answer.recommended_actions) ? answer.recommended_actions.map((a) => `Action (${a.priority || 'Next'}): ${a.action || ''} — ${a.why || ''}`) : []),
    ].join('\n');
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    } catch (_) {}
  };

  const buildExportPayload = () => ({
    model_title: getProcessModelTitle ? getProcessModelTitle() : '',
    question: selectedQuestion,
    generated_at: new Date().toISOString(),
    answer,
    evidence_summary: {
      process_summary: payloadBundle.inputPayload?.process_summary || {},
      canonical_health: payloadBundle.inputPayload?.canonical_health || {},
      data_coverage: payloadBundle.inputPayload?.data_coverage || {},
      sla_signals: payloadBundle.inputPayload?.sla_signals || {},
    },
  });

  const buildExportHtml = () => {
    const payload = buildExportPayload();
    const esc = (value) => String(value ?? '').replace(/[&<>]/g, (ch) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' }[ch]));
    const list = (items, render) => Array.isArray(items) && items.length
      ? `<ul>${items.map((item) => `<li>${render ? render(item) : esc(item)}</li>`).join('')}</ul>`
      : '<p class="muted">No items returned.</p>';
    const answerData = payload.answer || {};
    return `<!doctype html>
<html><head><meta charset="utf-8"/><title>Process Advisor Investigation</title>
<style>
body{font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;color:#172B4D;background:#F4F5F7;margin:0;padding:32px;line-height:1.45}.wrap{max-width:980px;margin:0 auto;background:#fff;border:1px solid #DFE1E6;border-radius:10px;box-shadow:0 2px 8px rgba(9,30,66,.10);overflow:hidden}.head{background:linear-gradient(135deg,#F0F4FF,#E8EEFF);padding:22px 26px;border-bottom:1px solid #DFE1E6}.eyebrow{font-size:11px;font-weight:800;color:#0C66E4;letter-spacing:.08em;text-transform:uppercase}.head h1{margin:6px 0 4px;font-size:24px;color:#0052CC}.meta{font-size:12px;color:#5E6C84}.body{padding:24px 26px}.question{border-left:4px solid #0C66E4;background:#F7F9FC;padding:14px 16px;border-radius:7px;margin-bottom:20px;font-weight:700}.summary{font-size:16px;font-weight:650;margin-bottom:18px}.section{margin-top:22px}.section h2{font-size:15px;margin:0 0 8px;color:#172B4D}.muted{color:#6B778C}.tag{display:inline-block;border:1px solid #B3D4FF;background:#E9F2FF;color:#0C66E4;border-radius:999px;padding:4px 10px;margin:3px;font-size:12px;font-weight:700}li{margin:6px 0}strong{color:#0C66E4}
</style></head><body><div class="wrap"><div class="head"><div class="eyebrow">Process Advisor Investigation</div><h1>${esc(payload.model_title || 'Investigation')}</h1><div class="meta">Generated ${esc(new Date(payload.generated_at).toLocaleString())}</div></div><div class="body"><div class="question">${esc(payload.question || '')}</div><div class="summary">${esc(answerData.answer_summary || '')}</div><div class="section"><h2>Key points</h2>${list(answerData.key_points, (p) => `<strong>${esc(p.label || 'Point')}:</strong> ${esc(p.detail || p.evidence || '')}`)}</div><div class="section"><h2>Recommended actions</h2>${list(answerData.recommended_actions, (a) => `<strong>${esc(a.priority || 'Next')}:</strong> ${esc(a.action || '')} ${a.why ? `— ${esc(a.why)}` : ''}`)}</div><div class="section"><h2>Evidence used</h2>${list(answerData.evidence_used)}</div><div class="section"><h2>Limits / validation needed</h2>${list(answerData.limits)}</div><div class="section"><h2>Follow-up questions</h2>${list(answerData.follow_up_questions)}</div></div></div></body></html>`;
  };

  const exportHtml = () => {
    if (!answer) return;
    const output = buildExportHtml();
    const blob = new Blob([output], { type: 'text/html;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `investigation-${(getProcessModelTitle?.() || 'process').replace(/\s+/g, '-').toLowerCase()}.html`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
    setIsExportModalOpen(false);
  };

  const exportPdf = () => {
    if (!answer) return;
    const win = window.open('', '_blank', 'width=1400,height=900');
    if (!win) return;
    win.document.open(); win.document.write(buildExportHtml()); win.document.close(); win.focus();
    setTimeout(() => win.print(), 300);
    setIsExportModalOpen(false);
  };

  const exportJson = () => {
    if (!answer) return;
    const blob = new Blob([JSON.stringify(buildExportPayload(), null, 2)], { type: 'application/json;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `investigation-${(getProcessModelTitle?.() || 'process').replace(/\s+/g, '-').toLowerCase()}.json`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
    setIsExportModalOpen(false);
  };

  return (
    <div className="pa-investigations">
      {/* Existing global CSS block remains intact */}
      <style>{`
        .pa-investigations {
          height: 100%;
          overflow: auto;
          background: #F4F5F7;
          padding: 12px;
          box-sizing: border-box;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
          color: #172B4D;
          font-size: 13px;
          line-height: 1.45;
        }

        .pa-inv-shell {
          max-width: 1480px;
          margin: 0 auto;
          display: flex;
          flex-direction: column;
          gap: 12px;
        }

        .pa-inv-hero {
          background: linear-gradient(135deg, #F0F4FF 0%, #E8EEFF 100%);
          color: #172B4D;
          border: 1px solid #D6E4FF;
          border-radius: 8px;
          padding: 10px 14px;
          box-shadow: 0 1px 3px rgba(9,45,66,0.08);
        }

        .pa-inv-hero-top {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
          flex-wrap: wrap;
        }

        .pa-inv-title {
          display: flex;
          align-items: center;
          gap: 12px;
          min-width: 0;
        }

        .pa-inv-icon {
          width: 36px;
          height: 36px;
          border-radius: 8px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: #0C66E4;
          color: #fff;
          flex-shrink: 0;
          overflow: hidden;
          box-shadow: 0 2px 4px rgba(9,30,66,0.14);
        }

        .pa-inv-hero h2 {
          margin: 0;
          font-size: 17px;
          line-height: 1.2;
          font-weight: 700;
          color: #0052CC;
          letter-spacing: -0.2px;
        }

        .pa-inv-hero p {
          margin: 3px 0 0;
          color: #5E6C84;
          font-size: 12px;
          max-width: 980px;
          line-height: 1.35;
          font-weight: 400;
        }

        .pa-inv-status {
          display: flex;
          gap: 8px;
          flex-wrap: wrap;
          justify-content: flex-end;
        }

        .pa-inv-export {
          background: #FFFFFF;
          color: #00875A;
          border: 1px solid #00875A;
          padding: 6px 12px;
          border-radius: 4px;
          font-size: 12px;
          line-height: 1.2;
          font-weight: 600;
          cursor: pointer;
          min-height: 30px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          white-space: nowrap;
        }

        .pa-inv-export:hover { background: #E3FCEF; }

        .pa-inv-grid {
          display: grid;
          grid-template-columns: minmax(340px, 410px) minmax(0, 1fr);
          gap: 12px;
          align-items: start;
        }

        .pa-inv-column {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }

        .pa-inv-card {
          background: #FFFFFF;
          border: 1px solid #DFE1E6;
          border-radius: 10px;
          box-shadow: 0 1px 4px rgba(9,30,66,0.08);
          overflow: hidden;
        }

        .pa-inv-card-header {
          padding: 11px 14px;
          border-bottom: 1px solid #EBECF0;
          display: flex;
          align-items: center;
          gap: 10px;
          background: #FAFBFC;
        }

        .pa-inv-card-suggested .pa-inv-card-header { background: linear-gradient(90deg, #F4F8FF 0%, #FFFFFF 100%); }
        .pa-inv-card-library .pa-inv-card-header { background: linear-gradient(90deg, #F7F5FF 0%, #FFFFFF 100%); }
        .pa-inv-card-ask .pa-inv-card-header { background: linear-gradient(90deg, #F3FBF7 0%, #FFFFFF 100%); }
        .pa-inv-card-answer .pa-inv-card-header { background: linear-gradient(90deg, #F4F8FF 0%, #FFFFFF 100%); }
        .pa-inv-card-history .pa-inv-card-header { background: linear-gradient(90deg, #FAFBFC 0%, #FFFFFF 100%); }

        .pa-inv-card-header h3 {
          margin: 0;
          font-size: 15px;
          font-weight: 650;
          color: #172B4D;
          letter-spacing: -0.1px;
        }

        .pa-inv-card-header span {
          font-size: 12px;
          color: #5E6C84;
          display: block;
          margin-top: 2px;
          line-height: 1.35;
          font-weight: 400;
        }

        .pa-inv-card-body { padding: 12px 14px; }

        .pa-inv-section-icon {
          width: 28px;
          height: 28px;
          border-radius: 7px;
          background: #E9F2FF;
          color: #0C66E4;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }

        .pa-inv-section-icon.purple { background: #F3F0FF; color: #6554C0; }
        .pa-inv-section-icon.green { background: #E3FCEF; color: #00875A; }
        .pa-inv-section-icon.neutral { background: #F4F5F7; color: #5E6C84; }

        .pa-inv-question-list {
          display: flex;
          flex-direction: column;
          gap: 7px;
        }

        .pa-inv-question {
          position: relative;
          border: 1px solid #DFE1E6;
          background: #FFFFFF;
          color: #172B4D;
          border-radius: 7px;
          padding: 9px 10px 9px 12px;
          text-align: left;
          cursor: pointer;
          font-size: 12.5px;
          line-height: 1.38;
          font-weight: 550;
          letter-spacing: 0;
          transition: border-color .15s, background .15s, color .15s, box-shadow .15s;
        }

        .pa-inv-question:before {
          content: '';
          position: absolute;
          left: 0;
          top: 9px;
          bottom: 9px;
          width: 3px;
          border-radius: 0 4px 4px 0;
          background: #B3D4FF;
          opacity: .85;
        }

        .pa-inv-card-library .pa-inv-question:before { background: #C0B6F2; }

        .pa-inv-question:hover {
          border-color: #B3D4FF;
          background: #F7F9FC;
          box-shadow: 0 1px 2px rgba(9,30,66,0.06);
        }

        .pa-inv-question.active {
          border-color: #0C66E4;
          background: #F0F6FF;
          color: #0747A6;
          box-shadow: 0 0 0 1px rgba(12,102,228,0.08);
        }

        .pa-inv-question-main {
          font-weight: 550;
          color: inherit;
        }

        .pa-inv-signal {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          font-size: 10.5px;
          font-weight: 600;
          color: #5E6C84;
          background: #F4F5F7;
          border: 1px solid #EBECF0;
          padding: 2px 6px;
          border-radius: 999px;
          letter-spacing: 0;
          margin-top: 3px;
        }

        .pa-inv-library-group {
          border: 1px solid #EBECF0;
          border-radius: 8px;
          background: #FFFFFF;
          margin-bottom: 8px;
          overflow: hidden;
        }

        .pa-inv-library-group:last-child { margin-bottom: 0; }

        .pa-inv-library-toggle {
          width: 100%;
          border: none;
          background: #FFFFFF;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 8px;
          padding: 9px 10px;
          cursor: pointer;
          color: #172B4D;
          font-family: inherit;
          text-align: left;
        }

        .pa-inv-library-toggle:hover { background: #F7F9FC; }

        .pa-inv-library-toggle-left {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          min-width: 0;
        }

        .pa-inv-chevron {
          color: #5E6C84;
          flex-shrink: 0;
          transition: transform .15s ease;
        }

        .pa-inv-chevron.open {
          transform: rotate(90deg);
          color: #0C66E4;
        }

        .pa-inv-library-title {
          font-size: 11px;
          text-transform: uppercase;
          letter-spacing: .06em;
          color: #42526E;
          font-weight: 700;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .pa-inv-count {
          flex-shrink: 0;
          background: #F7F5FF;
          color: #6554C0;
          border: 1px solid #DFD8FD;
          border-radius: 999px;
          padding: 2px 7px;
          font-size: 11px;
          font-weight: 700;
          line-height: 1.25;
        }

        .pa-inv-library-questions {
          padding: 0 10px 10px;
          border-top: 1px solid #F1F2F4;
          background: #FFFFFF;
        }

        .pa-inv-library-questions .pa-inv-question:first-child { margin-top: 10px; }

        .pa-inv-input-row {
          display: flex;
          gap: 10px;
          align-items: stretch;
        }

        .pa-inv-input-row textarea {
          flex: 1;
          min-height: 78px;
          resize: vertical;
          border: 1px solid #C1C7D0;
          border-radius: 8px;
          padding: 10px 12px;
          font-size: 13px;
          font-family: inherit;
          color: #172B4D;
          outline: none;
          line-height: 1.45;
          background: #FFFFFF;
        }

        .pa-inv-input-row textarea:focus {
          border-color: #0C66E4;
          box-shadow: 0 0 0 2px #E9F2FF;
        }

        .pa-inv-send {
          width: 92px;
          border: none;
          border-radius: 8px;
          background: #0C66E4;
          color: #fff;
          font-weight: 650;
          cursor: pointer;
          display: inline-flex;
          gap: 6px;
          align-items: center;
          justify-content: center;
          box-shadow: 0 2px 4px rgba(9,30,66,0.14);
        }

        .pa-inv-send:disabled {
          background: #DFE1E6;
          color: #97A0AF;
          box-shadow: none;
          cursor: not-allowed;
        }

        .pa-inv-card-answer {
          background: linear-gradient(180deg, #FFFFFF 0%, #F8FAFF 100%);
        }

        .pa-inv-answer-head {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
          background: linear-gradient(90deg, #F4F8FF 0%, #FFFFFF 100%);
          border-bottom-color: #CFE1FF;
        }

        .pa-inv-answer-head h3 {
          margin: 0;
          font-size: 15px;
          font-weight: 700;
          color: #092957;
        }

        .pa-inv-answer-head p {
          margin: 5px 0 0;
          font-size: 12px;
          color: #3E4C66;
          line-height: 1.45;
          font-weight: 500;
        }

        .pa-inv-copy {
          border: 1px solid #B3D4FF;
          background: #FFFFFF;
          color: #0C66E4;
          border-radius: 6px;
          padding: 7px 10px;
          font-size: 12px;
          font-weight: 650;
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          gap: 6px;
          flex-shrink: 0;
        }

        .pa-inv-empty {
          min-height: 260px;
          display: flex;
          flex-direction: column;
          justify-content: center;
          align-items: center;
          text-align: center;
          color: #5E6C84;
          padding: 34px;
          background: linear-gradient(180deg, #FFFFFF 0%, #FAFBFC 100%);
        }

        .pa-inv-empty h3 {
          color: #172B4D;
          margin: 12px 0 6px;
          font-size: 17px;
          font-weight: 600;
        }

        .pa-inv-empty p {
          max-width: 580px;
          line-height: 1.45;
          margin: 0;
          font-weight: 400;
        }

        .pa-inv-loading {
          padding: 32px;
          color: #5E6C84;
          display: flex;
          align-items: center;
          gap: 10px;
        }

        .pa-inv-spinner {
          width: 18px;
          height: 18px;
          border: 3px solid #E9F2FF;
          border-top-color: #0C66E4;
          border-radius: 50%;
          animation: paInvSpin .9s linear infinite;
        }

        @keyframes paInvSpin { to { transform: rotate(360deg); } }

        .pa-inv-error {
          margin: 12px 14px;
          padding: 10px 12px;
          border-radius: 8px;
          border: 1px solid #FFBDAD;
          background: #FFEBE6;
          color: #BF2600;
          font-size: 13px;
          font-weight: 600;
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .pa-inv-answer {
          margin: 12px 14px 16px;
          padding: 0;
          font-size: 13px;
          line-height: 1.58;
          color: #42526E;
        }

        .pa-inv-summary-card {
          position: relative;
          border: 1px solid #9CC8FF;
          border-left: 5px solid #0C66E4;
          border-radius: 10px;
          background: linear-gradient(180deg, #F2F7FF 0%, #EAF3FF 100%);
          box-shadow: 0 4px 12px rgba(9, 30, 66, 0.08);
          padding: 13px 15px 14px;
          margin-bottom: 14px;
        }

        .pa-inv-summary-label {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          margin-bottom: 7px;
          color: #0C66E4;
          font-size: 11px;
          font-weight: 800;
          text-transform: uppercase;
          letter-spacing: .07em;
        }

        .pa-inv-summary {
          margin: 0;
          font-size: 14px;
          line-height: 1.62;
          font-weight: 650;
          color: #092957;
        }

        .pa-inv-answer-section {
          margin-top: 14px;
          padding: 12px;
          border: 1px solid #E6EAF2;
          background: rgba(255,255,255,0.82);
          border-radius: 10px;
        }

        .pa-inv-section-label {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 10px;
          margin: 0 0 9px;
          font-size: 11px;
          text-transform: uppercase;
          letter-spacing: .06em;
          color: #344563;
          font-weight: 800;
        }

        .pa-inv-section-label em {
          font-style: normal;
          text-transform: none;
          letter-spacing: 0;
          font-size: 11px;
          color: #6B778C;
          font-weight: 650;
        }

        .pa-inv-section-label.warning { color: #974F0C; }

        .pa-inv-answer h4 {
          margin: 0 0 8px;
          font-size: 11px;
          text-transform: uppercase;
          letter-spacing: .06em;
          color: #42526E;
          font-weight: 700;
        }

        .pa-inv-list {
          display: flex;
          flex-direction: column;
          gap: 9px;
        }

        .pa-inv-keypoint {
          border: 1px solid #DFE6F3;
          border-left: 4px solid #B3D4FF;
          background: #FFFFFF;
          border-radius: 9px;
          padding: 10px 12px;
          box-shadow: 0 1px 2px rgba(9,30,66,0.04);
        }

        .pa-inv-keypoint strong,
        .pa-inv-action strong {
          display: block;
          font-size: 13px;
          color: #172B4D;
          margin-bottom: 4px;
          font-weight: 700;
        }

        .pa-inv-keypoint p,
        .pa-inv-action p {
          margin: 0;
          color: #344563;
          font-weight: 400;
        }

        .pa-inv-keypoint small {
          display: block;
          margin-top: 7px;
          padding-top: 6px;
          border-top: 1px solid #F1F2F4;
          color: #6B778C;
          font-size: 11px;
          line-height: 1.4;
          font-weight: 500;
        }

        .pa-inv-action {
          display: flex;
          gap: 11px;
          align-items: flex-start;
          background: #FFFFFF;
          border: 1px solid #DDEBE6;
          border-left: 4px solid #36B37E;
          border-radius: 9px;
          padding: 11px 12px;
          box-shadow: 0 1px 2px rgba(9,30,66,0.04);
        }

        .pa-inv-priority {
          flex-shrink: 0;
          border-radius: 999px;
          padding: 2px 7px;
          font-size: 11px;
          font-weight: 700;
        }

        .pa-inv-evidence-section {
          background: #F8FAFC;
        }

        .pa-inv-bullets,
        .pa-inv-limits ul {
          margin: 0;
          padding-left: 18px;
          color: #42526E;
        }

        .pa-inv-bullets li,
        .pa-inv-limits li {
          margin: 4px 0;
        }

        .pa-inv-limits {
          margin-top: 14px;
          background: #FFFAE6;
          border: 1px solid #FFE380;
          border-left: 4px solid #FFAB00;
          border-radius: 10px;
          padding: 11px 12px;
        }

        .pa-inv-limits h4 { color: #974F0C; }

        .pa-inv-chip-row {
          display: flex;
          flex-wrap: wrap;
          gap: 7px;
        }

        .pa-inv-chip-row button {
          border: 1px solid #B3D4FF;
          background: #E9F2FF;
          color: #0C66E4;
          border-radius: 999px;
          padding: 6px 10px;
          font-size: 12px;
          font-weight: 600;
          cursor: pointer;
        }

        .pa-inv-history {
          display: flex;
          flex-direction: column;
          gap: 7px;
        }

        .pa-inv-history button {
          border: 1px solid #DFE1E6;
          background: #FFFFFF;
          border-radius: 7px;
          padding: 8px 10px;
          text-align: left;
          cursor: pointer;
          color: #42526E;
          font-size: 12px;
          line-height: 1.35;
          font-weight: 500;
        }

        .pa-inv-history button:hover {
          background: #F7F9FC;
          border-color: #B3D4FF;
        }

        @media (max-width: 980px) {
          .pa-inv-grid { grid-template-columns: 1fr; }
          .pa-inv-hero-top { flex-direction: column; align-items: flex-start; }
          .pa-inv-status { justify-content: flex-start; }
          .pa-inv-input-row { flex-direction: column; }
          .pa-inv-send { width: 100%; min-height: 38px; }
        }
      `}</style>

      <div className="pa-inv-shell">
        <div className="pa-inv-hero">
          <div className="pa-inv-hero-top">
            <div className="pa-inv-title">
              <div className="pa-inv-icon"><Search size={22} /></div>
              <div>
                <h2>Ask Advisor</h2>
                <p>
                  Ask targeted questions about this {String(processLabel || 'process').toLowerCase()} model. Answers are grounded in Process Advisor evidence: metrics, variants, transitions, SLA signals, field quality and sample {String(entityLabel || 'work item').toLowerCase()} data.
                </p>
              </div>
            </div>
            <div className="pa-inv-status">
              {answer && <button type="button" className="pa-inv-export" onClick={() => setIsExportModalOpen(true)}>Export...</button>}
            </div>
          </div>
        </div>

        <div className="pa-inv-grid">
          <div className="pa-inv-column">
            
            {/* SUGGESTED FROM THIS MODEL — UPDATED WITH METHODOLOGY BADGES AND AREA FOCUS POPULATE */}
            <div className="pa-inv-card pa-inv-card-suggested">
              <div className="pa-inv-card-header">
                <span className="pa-inv-section-icon"><Sparkles size={16} /></span>
                <div><h3>Suggested from this model</h3><span>Questions created from the strongest signals in the current data</span></div>
              </div>
              <div className="pa-inv-card-body pa-inv-question-list">
                {dataDrivenQuestions.map((q, idx) => (
                  <button 
                    key={idx} 
                    type="button" 
                    className={`pa-inv-question ${selectedQuestion === q.text ? 'active' : ''}`} 
                    onClick={() => {
                      setCustomQuestion(q.text);
                      document.querySelector('.pa-inv-input-row textarea')?.focus();
                    }} 
                    disabled={isAsking}
                    style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '4px' }}
                  >
                    <span className="pa-inv-question-main">{q.text}</span>
                    <span className="pa-inv-signal">Signal: {q.trigger}</span>
                  </button>
                ))}
              </div>
            </div>

            <div className="pa-inv-card pa-inv-card-library">
              <div className="pa-inv-card-header">
                <span className="pa-inv-section-icon purple"><BookOpen size={16} /></span>
                <div><h3>Question library</h3><span>Reusable consultant and customer questions</span></div>
              </div>
              <div className="pa-inv-card-body">
                {QUESTION_LIBRARY.map((group) => {
                  const isOpen = expandedLibraryGroups[group.group] === true;
                  return (
                    <div className="pa-inv-library-group" key={group.group}>
                      <button
                        type="button"
                        className="pa-inv-library-toggle"
                        onClick={() => toggleLibraryGroup(group.group)}
                        aria-expanded={isOpen}
                      >
                        <span className="pa-inv-library-toggle-left">
                          <ChevronRight size={15} className={`pa-inv-chevron ${isOpen ? 'open' : ''}`} />
                          <span className="pa-inv-library-title">{group.group}</span>
                        </span>
                        <span className="pa-inv-count">{group.questions.length}</span>
                      </button>
                      {isOpen && (
                        <div className="pa-inv-library-questions pa-inv-question-list">
                          {group.questions.map((q, idx) => (
                            <button key={idx} type="button" className={`pa-inv-question ${selectedQuestion === q ? 'active' : ''}`} onClick={() => ask(q)} disabled={isAsking}><span className="pa-inv-question-main">{q}</span></button>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          <div className="pa-inv-column">
            <div className="pa-inv-card pa-inv-card-ask">
              <div className="pa-inv-card-header">
                <span className="pa-inv-section-icon green"><MessageSquare size={16} /></span>
                <div><h3>Ask your own question</h3><span>The prompt is automatically grounded with the model's standard data payload</span></div>
              </div>
              <div className="pa-inv-card-body">
                <div className="pa-inv-input-row">
                  <textarea value={customQuestion} onChange={(e) => setCustomQuestion(e.target.value)} placeholder="Example: Which resolver groups should we focus on first to reduce SLA breaches?" disabled={isAsking} onKeyDown={(e) => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) ask(customQuestion); }} />
                  <button className="pa-inv-send" type="button" onClick={() => ask(customQuestion)} disabled={isAsking || !normaliseQuestion(customQuestion)}>{isAsking ? 'Asking…' : <><Send size={15} /> Ask</>}</button>
                </div>
              </div>
            </div>

            <div className="pa-inv-card pa-inv-card-answer">
              <div className="pa-inv-card-header pa-inv-answer-head">
                <div>
                  <h3>{selectedQuestion ? 'Investigation answer' : 'Answer panel'}</h3>
                  <p>{selectedQuestion || 'Select a suggested question, choose from the library, or ask your own.'}</p>
                </div>
                {answer && <button className="pa-inv-copy" type="button" onClick={copyAnswer}><Copy size={14} />{copied ? 'Copied' : 'Copy'}</button>}
              </div>
              {error && <div className="pa-inv-error"><AlertTriangle size={16} />{error}</div>}
              {isAsking && <div className="pa-inv-loading"><span className="pa-inv-spinner" /> Analysing process data and generating a grounded answer…</div>}
              {!isAsking && !answer && !error && (
                <div className="pa-inv-empty">
                  <Search size={38} color="#0C66E4" />
                  <h3>Use the app’s collected data to ask better questions</h3>
                  <p>Ask Advisor turns the process mining, SLA, activity, attribute and sample data into targeted answers and practical next actions.</p>
                </div>
              )}
              {!isAsking && answer && <AnswerView answer={answer} onAsk={ask} />}
            </div>

            {history.length > 0 && (
              <div className="pa-inv-card pa-inv-card-history">
                <div className="pa-inv-card-header">
                  <span className="pa-inv-section-icon neutral"><MessageSquare size={16} /></span>
                  <div><h3>Recent questions</h3><span>This session only</span></div>
                </div>
                <div className="pa-inv-card-body pa-inv-history">
                  {history.slice(0, 5).map((turn, idx) => <button key={idx} type="button" onClick={() => { setSelectedQuestion(turn.question); setAnswer(turn.answer); }}>{turn.question}</button>)}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      <ExportModal
        isOpen={isExportModalOpen}
        onClose={() => setIsExportModalOpen(false)}
        title="Export Investigation"
        onExportHTML={exportHtml}
        onExportPDF={exportPdf}
        onExportJSON={exportJson}
      />
    </div>
  );
}